/**
 * Theme definitions for LexAI Dashboard
 * Supports light and dark modes with Obsidian-inspired styling
 */

export type ThemeMode = 'light' | 'dark';

export interface ThemeColors {
  // Core backgrounds
  '--obsidian-bg': string;
  '--obsidian-sidebar-bg': string;
  '--obsidian-hover-bg': string;

  // Text colors
  '--obsidian-text-primary': string;
  '--obsidian-text-secondary': string;
  '--obsidian-text-disabled': string;
  '--obsidian-text-muted': string;

  // Accent colors
  '--obsidian-accent': string;
  '--obsidian-accent-bg': string;
  '--obsidian-accent-10': string;

  // Borders and dividers
  '--obsidian-border': string;
  '--obsidian-item-hover': string;

  // Canvas colors
  '--canvas-bg-primary': string;
  '--canvas-bg-secondary': string;
  '--canvas-text-primary': string;
  '--canvas-text-secondary': string;
  '--canvas-text-muted': string;
  '--canvas-border': string;
  '--canvas-accent': string;
  '--canvas-accent-bg': string;

  // Status colors
  '--canvas-success': string;
  '--canvas-danger': string;
  '--canvas-warning': string;

  // Legacy support
  '--dark-bg': string;
  '--dark-secondary': string;
  '--dark-tertiary': string;
  '--dark-border': string;
}

export const darkTheme: ThemeColors = {
  // Core backgrounds - dark slate
  '--obsidian-bg': '#1e293b',
  '--obsidian-sidebar-bg': '#293548',
  '--obsidian-hover-bg': '#334155',

  // Text colors - light for contrast
  '--obsidian-text-primary': '#e2e8f0',
  '--obsidian-text-secondary': '#94a3b8',
  '--obsidian-text-disabled': '#64748b',
  '--obsidian-text-muted': '#8a9a9a',

  // Accent - blue
  '--obsidian-accent': '#3b82f6',
  '--obsidian-accent-bg': 'rgba(59, 130, 246, 0.15)',
  '--obsidian-accent-10': 'rgba(59, 130, 246, 0.1)',

  // Borders
  '--obsidian-border': '#334155',
  '--obsidian-item-hover': '#334155',

  // Canvas (brand colors)
  '--canvas-bg-primary': '#303a46',
  '--canvas-bg-secondary': '#375458',
  '--canvas-text-primary': '#e3efe3',
  '--canvas-text-secondary': '#b8c5c5',
  '--canvas-text-muted': '#8a9a9a',
  '--canvas-border': '#4a5a66',
  '--canvas-accent': '#75b2b8',
  '--canvas-accent-bg': 'rgba(117, 178, 184, 0.15)',

  // Status
  '--canvas-success': '#6bb896',
  '--canvas-danger': '#e07a7a',
  '--canvas-warning': '#d4a574',

  // Legacy
  '--dark-bg': '#1e293b',
  '--dark-secondary': '#293548',
  '--dark-tertiary': '#334155',
  '--dark-border': '#334155',
};

export const lightTheme: ThemeColors = {
  // Core backgrounds - light
  '--obsidian-bg': '#ffffff',
  '--obsidian-sidebar-bg': '#f8fafc',
  '--obsidian-hover-bg': '#f1f5f9',

  // Text colors - dark for contrast
  '--obsidian-text-primary': '#1e293b',
  '--obsidian-text-secondary': '#64748b',
  '--obsidian-text-disabled': '#94a3b8',
  '--obsidian-text-muted': '#94a3b8',

  // Accent - blue (same as dark)
  '--obsidian-accent': '#3b82f6',
  '--obsidian-accent-bg': 'rgba(59, 130, 246, 0.1)',
  '--obsidian-accent-10': 'rgba(59, 130, 246, 0.05)',

  // Borders
  '--obsidian-border': '#e2e8f0',
  '--obsidian-item-hover': '#f1f5f9',

  // Canvas (lighter versions of brand)
  '--canvas-bg-primary': '#ffffff',
  '--canvas-bg-secondary': '#f8fafc',
  '--canvas-text-primary': '#1e293b',
  '--canvas-text-secondary': '#64748b',
  '--canvas-text-muted': '#94a3b8',
  '--canvas-border': '#e2e8f0',
  '--canvas-accent': '#3b82f6',
  '--canvas-accent-bg': 'rgba(59, 130, 246, 0.1)',

  // Status
  '--canvas-success': '#22c55e',
  '--canvas-danger': '#ef4444',
  '--canvas-warning': '#f59e0b',

  // Legacy (light equivalents)
  '--dark-bg': '#ffffff',
  '--dark-secondary': '#f8fafc',
  '--dark-tertiary': '#f1f5f9',
  '--dark-border': '#e2e8f0',
};

export const themes: Record<ThemeMode, ThemeColors> = {
  dark: darkTheme,
  light: lightTheme,
};

/**
 * Apply theme to document root
 */
export const applyTheme = (theme: ThemeMode): void => {
  const root = document.documentElement;
  const colors = themes[theme];

  Object.entries(colors).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });

  // Set data attribute for CSS selectors
  root.setAttribute('data-theme', theme);
};

/**
 * Get stored theme preference or system preference
 */
export const getStoredTheme = (): ThemeMode => {
  const stored = localStorage.getItem('lex-theme') as ThemeMode | null;
  if (stored && (stored === 'light' || stored === 'dark')) {
    return stored;
  }

  // Fall back to system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }

  return 'dark'; // Default to dark
};

/**
 * Store theme preference
 */
export const storeTheme = (theme: ThemeMode): void => {
  localStorage.setItem('lex-theme', theme);
};
